// Tool レジストリ。AI秘書が使えるツールはここに登録したものだけ。
//
// 【安全装置】
// - 登録されていないツール名は実行しない（LLMの幻覚で存在しない関数を呼ばせない）
// - 引数は必ず Zod で検証する（型が違えば実行しない）
// - 書込ツール（describe/create/update_record）は WRITE_ENABLED のときだけ登録される。
//   OFF なら LLM からは書込が物理的に存在しない（削除ツールはどの状態でも存在しない）。
import { WRITE_ENABLED } from '../config';
import {
  listRecordsTool,
  getRecordTool,
  describeResourceTool,
  createRecordTool,
  updateRecordTool,
} from './generic.tools';
import type { SecretaryTool, ToolCall, ToolContext, ToolDefinition } from './types';

/**
 * 引数の型を消したツール。
 * ツールごとに引数型が異なるため、そのままでは1つのレジストリに入れられない。
 * register() で型をクロージャの中に閉じ込め、外からは unknown で扱えるようにする。
 */
interface RegisteredTool {
  definition: ToolDefinition;
  run: (rawArgs: unknown, ctx: ToolContext) => Promise<unknown>;
}

/** SecretaryTool<TArgs> を検証つきで型消去し、レジストリに載せられる形にする。 */
function register<TArgs>(tool: SecretaryTool<TArgs>): RegisteredTool {
  return {
    definition: tool.definition,
    async run(rawArgs, ctx) {
      const parsed = tool.schema.safeParse(rawArgs);
      if (!parsed.success) {
        const reason = parsed.error.issues.map((issue) => issue.message).join(' / ');
        return { error: `引数が不正です: ${reason}` };
      }
      return tool.execute(parsed.data, ctx);
    },
  };
}

/** 書込ツールの名前（書込回数の上限判定に使う）。 */
export const WRITE_TOOL_NAMES = ['create_record', 'update_record'] as const;

/** 登録済みツール。書込3種は WRITE_ENABLED のときだけ存在する。 */
const TOOLS: Record<string, RegisteredTool> = {
  list_records: register(listRecordsTool),
  get_record: register(getRecordTool),
  ...(WRITE_ENABLED
    ? {
        describe_resource: register(describeResourceTool),
        create_record: register(createRecordTool),
        update_record: register(updateRecordTool),
      }
    : {}),
};

/** LLM へ渡すツール定義の一覧。 */
export function toolDefinitions(): ToolDefinition[] {
  return Object.values(TOOLS).map((tool) => tool.definition);
}

/** tool_call の arguments はオブジェクト or JSON文字列で来るため、両方を吸収する。 */
function parseArguments(raw: ToolCall['function']['arguments']): unknown {
  if (typeof raw !== 'string') return raw ?? {};
  if (raw.trim() === '') return {};
  return JSON.parse(raw); // 失敗時は runTool の catch で日本語化する
}

/**
 * LLM が要求したツールを実行し、LLM へ返す結果を得る。
 * 失敗しても例外は投げず、エラー内容を結果として返す（LLMが状況を説明できるようにする）。
 * ctx（ログイン中ユーザー）は書込ツールが「誰の権限で」実行するかに使う。
 */
export async function runTool(call: ToolCall, ctx: ToolContext = { userId: null }): Promise<unknown> {
  const name = call.function?.name;
  const tool = name ? TOOLS[name] : undefined;

  if (!tool) {
    return { error: `「${name ?? '不明'}」というツールは存在しません` };
  }

  try {
    return await tool.run(parseArguments(call.function.arguments), ctx);
  } catch (error: unknown) {
    // 引数のJSON不正、Firestoreの権限エラー等。UIに英語を出さないため日本語で返す。
    const message = error instanceof Error ? error.message : '想定外のエラーが発生しました';
    return { error: `「${name}」の実行に失敗しました: ${message}` };
  }
}
